import React, { memo } from 'react';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { CircularProgress, IconButton } from '@mui/material';
import { withStyles } from '@mui/styles';
import PropTypes from 'prop-types';

import { isMobile } from '../../services/isMobile';
import MenuItem from '../MenuItem/MenuItem';
import Select from '../Select/Select';
import Button, { ExportIcon } from '../Button/Button';

const exportButtonStyle = { marginLeft: '10px' };

const Loading = withStyles({
  root: {
    marginRight: '10px',
    color: '#777',
  },
})(CircularProgress);

const ListPagination = memo(
  ({
    onPageChange,
    page,
    pages,
    showPageSizeOptions,
    pageSizeOptions,
    pageSize,
    canPrevious,
    canNext,
    onPageSizeChange,
    className,
    paginationStyle,
    dashboardMobile,
    totalCount,
    renderOptButton,
    isExporting,
    selectAll,
    selectedDataIds,
    sortedData,
    offlineFiltering,
    exportLimit,
    confirm,
    onExportTable,
    exportable,
  }) => {
    const getSafePage = (pageParam) => {
      if (isNaN(pageParam)) {
        pageParam = page;
      }

      return Math.min(Math.max(pageParam, 0), pages - 1);
    };

    const changePage = (pageParam) => {
      pageParam = getSafePage(pageParam);

      if (page !== pageParam) {
        onPageChange(pageParam);
      }
    };

    const exportTable = () => {
      const exportCount = selectAll ? totalCount : selectedDataIds.length;

      if (exportLimit && exportCount > exportLimit) {
        confirm(
          `Maximum number of exported records is ${exportLimit}. Do you want to proceed with exporting this portion of your query result?`,
          onExportTable,
        );
      } else {
        onExportTable();
      }
    };

    const allCount = offlineFiltering ? sortedData.length : totalCount;
    const itemFrom = allCount ? page * pageSize + 1 : 0;
    const itemTo = Math.min(allCount, (page + 1) * pageSize);
    const allItems = allCount;
    const prevPage = page - 1;
    const nextPage = page + 1;
    const disableExportButton = !(selectAll || (selectedDataIds && selectedDataIds.length > 0)) || isExporting;

    const exportTitle = (
      <span style={{ marginLeft: isMobile ? 0 : 5 }}>
        Export {selectedDataIds && selectedDataIds.length > 0 && `(${selectedDataIds.length})`}
      </span>
    );

    return (
      <div className={clsx(className, `-pagination ${dashboardMobile ? 'dashboard-customized' : ''}`)} style={paginationStyle}>
        {showPageSizeOptions && (
          <span className="-pageSizeOptions">
            {!dashboardMobile && <>Rows per page:&nbsp;</>}
            <Select
              value={pageSize}
              className={`pageSizeSelect ${isMobile ? 'mobile' : ''}`}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
            >
              {pageSizeOptions.map((option, i) => (
                <MenuItem key={i} value={option} title={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            {renderOptButton}
            {exportable && (
              <Button
                color="primary"
                style={exportButtonStyle}
                className="table-button-with-span"
                disabled={disableExportButton}
                onClick={exportTable}
              >
                {isExporting ? <Loading size={20} /> : <ExportIcon />}
                {isMobile ? '' : exportTitle}
              </Button>
            )}
          </span>
        )}
        <div className="-pageJumpBlock">
          {!isMobile && <span className="">Showing</span>}
          <div className="">
            <IconButton
              size="large"
              onClick={() => {
                if (!canPrevious) return;
                changePage(prevPage);
              }}
              disabled={!canPrevious}
            >
              <ChevronLeft />
            </IconButton>
          </div>
          <span className="">
            {itemFrom} to {itemTo}
          </span>
          &nbsp;
          <div className="">
            <IconButton
              size="large"
              onClick={() => {
                if (!canNext) return;
                changePage(nextPage);
              }}
              disabled={!canNext}
            >
              <ChevronRight />
            </IconButton>
          </div>
          <span>
            of&nbsp;
            <span className="">{allItems}</span>
          </span>
        </div>
      </div>
    );
  },
);

ListPagination.propTypes = {
  totalCount: PropTypes.number,
  page: PropTypes.number,
  showPageSizeOptions: PropTypes.bool,
  pageSizeOptions: PropTypes.array,
  pageSize: PropTypes.number,
  canPrevious: PropTypes.bool,
  canNext: PropTypes.bool,
  onPageSizeChange: PropTypes.func,
  className: PropTypes.string,
  paginationStyle: PropTypes.object,
  dashboardMobile: PropTypes.bool,
};

export default ListPagination;
